import { Box } from '@mui/material';
import Divider from 'components/divider';
import FilterButtonGroup from 'components/filter-button-group';
import List, { HeadCell } from 'components/list/list';
import LoadingSpinner from 'components/loading-spinner';
import { memo, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useAccountState } from 'contexts/account/account-context';
import { normalizeName } from 'utils/list-sort';
import { useSitesState } from 'views/order-feed/context/sites-context';

import { MySupplyPlanParams } from 'views/order-feed/features/my-supply-plan/types/my-supply-plan-params.type';
import { SiteListItem } from 'views/order-feed/features/my-supply-plan/types/ofd-sites.type';
import { SiteTab } from 'views/order-feed/features/my-supply-plan/types/site-tab.enum';

import { SiteListColumns } from 'views/order-feed/features/my-supply-plan/components/site-list-columns';
import SiteListRow from 'views/order-feed/features/my-supply-plan/components/site-list-row';

const PAGE_NAME_ID = 'orderFeed.listOrderSupplyPlan';

function compareSiteNames(a: SiteListItem, b: SiteListItem) {
  return normalizeName(a.siteName).localeCompare(normalizeName(b.siteName));
}

function compareOfdStatus(firstSite: SiteListItem, secondSite: SiteListItem) {
  const firstEnabled = firstSite.isOfd1Enabled || firstSite.isOfd2Enabled;
  const secondEnabled = secondSite.isOfd1Enabled || secondSite.isOfd2Enabled;
  return Number(firstEnabled) - Number(secondEnabled);
}

const SiteList: React.FC = () => {
  const intl = useIntl();
  const { account } = useAccountState();
  const { siteTab } = useParams<MySupplyPlanParams>();
  const {
    sitesQuery: { data: sitesData, isLoading: sitesLoading, isFetching: sitesFetching, isFetched: sitesFetched },
    siteOverviewQuery: { data: overviewData, isLoading: overviewLoading, isFetching: overviewFetching, isFetched: overviewFetched },
  } = useSitesState();

  const [filters, setFilters] = useState<string[]>([]);
  const isSiteTabInactive = siteTab === SiteTab.INACTIVE;
  const hasOrderFreeDelivery = account.company?.features.hasOrderFreeDelivery;
  const hasOrderFreeDelivery2 = account.company?.features.hasOrderFreeDelivery2;
  const columns = useMemo(() => SiteListColumns(intl, Boolean(hasOrderFreeDelivery2)), [intl, hasOrderFreeDelivery2]);

  const basicList: SiteListItem[] = useMemo(() => {
    const rows = (sitesData ?? []).map(
      (site): SiteListItem => ({
        siteName: site.name,
        isOfd1Enabled: site.isOfd1Enabled,
        isOfd2Enabled: site.isOfd2Enabled,
        hasRecentOrders: site.hasRecentOrders,
        globalSiteId: site.globalSiteId,
        siteErpId: site.siteErpId,
        inventory: 0,
        weeklyConsumption: 0,
        hasAutomaticOrderingOfd2: false,
        siteIntensity: 0,
        week1Deliveries: [0],
        week2Deliveries: [0],
        week3Deliveries: [0],
        scheduleTooShort: false,
        productsNotAvailable: false,
      })
    );

    return rows.sort(compareSiteNames);
  }, [sitesData]);

  const overviewBySiteId = useMemo(() => {
    const map = new Map<string, Partial<SiteListItem>>();
    for (const o of overviewData ?? []) map.set(o.globalSiteId, o);
    return map;
  }, [overviewData]);

  const activeSites: SiteListItem[] = useMemo(() => {
    const onlyActive = basicList.filter((s) => s.hasRecentOrders);
    if (!onlyActive.length) return onlyActive;

    return onlyActive.map((activeSite) => {
      const site = overviewBySiteId.get(activeSite.globalSiteId);
      if (!site) return activeSite;
      return {
        ...activeSite,
        ...site,
        siteErpId: site.siteErpId || activeSite.siteErpId,
        hasRecentOrders: activeSite.hasRecentOrders,
      } as SiteListItem;
    });
  }, [basicList, overviewBySiteId]);

  const inactiveSites: SiteListItem[] = useMemo(() => basicList.filter((s) => !s.hasRecentOrders), [basicList]);

  const requiresActionCount = useMemo(
    () => activeSites.filter((site) => site.productsNotAvailable || site.scheduleTooShort).length,
    [activeSites]
  );

  const visibleActiveSites: SiteListItem[] = useMemo(() => {
    if (!filters.includes('requireAction')) return activeSites;

    return activeSites.filter((site) => site.scheduleTooShort || site.productsNotAvailable);
  }, [activeSites, filters]);

  const isOverviewPending = !overviewFetched || overviewLoading || overviewFetching;
  const showFetchingColumns = !isSiteTabInactive && activeSites.length > 0 && isOverviewPending;

  const columnsForCurrentView = useMemo(() => {
    if (isSiteTabInactive) return columns.inactiveSitesColumns;
    if (!hasOrderFreeDelivery && !hasOrderFreeDelivery2) return columns.noOrderFreeDeliveryColumns;
    if (showFetchingColumns) return columns.isFetchingSitesColumns;
    return columns.activeSitesColumns;
  }, [columns, hasOrderFreeDelivery, hasOrderFreeDelivery2, isSiteTabInactive, showFetchingColumns]);

  const options: HeadCell<SiteListItem>[] = useMemo(
    () =>
      columnsForCurrentView.map((column) => {
        if (column.id === 'siteName') return { ...column, comparator: compareSiteNames };
        if (column.id === 'ofd') return { ...column, comparator: compareOfdStatus };
        return column;
      }),
    [columnsForCurrentView]
  );

  const rows = isSiteTabInactive ? inactiveSites : visibleActiveSites;

  const isSitesLoading = !sitesFetched && (sitesLoading || sitesFetching);

  const [orderedRows, setOrderedRows] = useState<SiteListItem[]>(rows);

  useEffect(() => {
    setOrderedRows(rows);
  }, [rows]);

  const listKey = `${siteTab ?? ''}-${filters.join(',')}-${showFetchingColumns}-${isSitesLoading}`;

  const filterButtons = [
    {
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.siteList.requireActionLabel` }, { value: requiresActionCount }),
      value: 'requireAction',
      length: requiresActionCount,
      state: true,
      sx: { width: 230 },
    },
  ];

  return (
    <Box display="flex" flexDirection="column" gap={4}>
      {!isSiteTabInactive && hasOrderFreeDelivery2 && (
        <Box display="flex" justifyContent="start" width="100%">
          {showFetchingColumns || isSitesLoading || overviewLoading ? (
            <Box height={30} display="flex" alignItems="center" justifyContent="center">
              <LoadingSpinner small />
            </Box>
          ) : (
            <Box width="100%" rowGap={4} display="flex" flexDirection="column">
              <FilterButtonGroup value="group" buttons={filterButtons} handleClick={setFilters} />
              <Divider />
            </Box>
          )}
        </Box>
      )}

      <List
        key={listKey}
        rows={rows}
        sort={setOrderedRows}
        options={options}
        isLoading={isSitesLoading}
        noResult={{
          show: rows.length === 0,
          text: <FormattedMessage id={`${PAGE_NAME_ID}.siteList.noResults`} />,
        }}>
        {orderedRows.map((site, idx) => (
          <SiteListRow site={site} key={site.globalSiteId ?? idx} />
        ))}
      </List>
    </Box>
  );
};

export default memo(SiteList);
